import {
  CHECKOUT_KEY,
  CONDITIONS_KEY,
  HIGH_CARE_CONDITIONS_QUERY_KEY,
  PARAMS_KEY,
  PRICING_KEY,
  PROSPECT_STATUS_QUERY_KEY,
  STRIPE_INTENT_KEY,
} from '../models/queries';

import type { Commands } from '../models/commands';
import type { QueryKeys } from '../models/queries';
import type { InvalidationReducer } from '@insify/funnel-components';

const assertNever = (x: never): never => {
  throw new Error('Unexpected object: ' + x);
};

type CommandKeys = Commands['type'];

export const commandQueryReducer: InvalidationReducer<CommandKeys, QueryKeys> = (state, command) => {
  if (command.commandType === 'reset') {
    for (const id of command.resetIds) {
      delete state[id];
    }
    return { ...state };
  }

  const { commandType, dispatchId } = command;
  switch (commandType) {
    case 'searchCompany':
    case 'requestSupport':
    case 'usePartner':
    case 'useRedirectingPartner':
    case 'requestSaveQuoteEmail':
    case 'requestQuoteEmail':
    case 'searchActivities': {
      return { ...state };
    }
    case 'selectCompany':
      state[dispatchId] = [
        PARAMS_KEY,
        CONDITIONS_KEY,
        HIGH_CARE_CONDITIONS_QUERY_KEY,
        PRICING_KEY,
        PROSPECT_STATUS_QUERY_KEY,
      ];
      return { ...state };
    case 'enterCompanyDetails':
      state[dispatchId] = [PARAMS_KEY, CONDITIONS_KEY, PROSPECT_STATUS_QUERY_KEY];
      return { ...state };
    case 'confirmCompanyNotYetRegistered':
    case 'enterAddress':
      state[dispatchId] = [PARAMS_KEY, HIGH_CARE_CONDITIONS_QUERY_KEY, PROSPECT_STATUS_QUERY_KEY];
      return { ...state };
    case 'confirmActivitySpecificFields':
      state[dispatchId] = [PARAMS_KEY, HIGH_CARE_CONDITIONS_QUERY_KEY, PRICING_KEY, PROSPECT_STATUS_QUERY_KEY];
      return { ...state };
    case 'enterHeadCount':
    case 'enterAnnualRevenue':
    case 'addActivity':
    case 'removeActivity':
    case 'selectDeductible':
    case 'selectProfessionalLiabilityCoverLimit':
    case 'selectPublicLiabilityCoverLimit':
    case 'selectElectronicsSumInsured':
    case 'selectInventorySumInsured':
    case 'selectMediaEquipmentSumInsured':
    case 'selectStockSumInsured':
    case 'selectToolsSumInsured': {
      state[dispatchId] = [
        PARAMS_KEY,
        CONDITIONS_KEY,
        PRICING_KEY,
        HIGH_CARE_CONDITIONS_QUERY_KEY,
        CHECKOUT_KEY,
        PROSPECT_STATUS_QUERY_KEY,
      ];
      return { ...state };
    }
    case 'requestPricing': {
      state[dispatchId] = [STRIPE_INTENT_KEY, CHECKOUT_KEY, PROSPECT_STATUS_QUERY_KEY];
      return { ...state };
    }
    case 'confirmPastClaims':
    case 'confirmPastClaimsSumWithinThreshold':
    case 'addPastClaim':
    case 'removePastClaim':
    case 'changePastClaim':
    case 'givePastInsuranceIssuesConsent': {
      state[dispatchId] = [
        PARAMS_KEY,
        CONDITIONS_KEY,
        HIGH_CARE_CONDITIONS_QUERY_KEY,
        PRICING_KEY,
        PROSPECT_STATUS_QUERY_KEY,
      ];
      return { ...state };
    }
    case 'confirmSegmentSpecificExclusionsAwareness':
    case 'confirmProductCertificationAwareness':
    case 'confirmActiveClaimsAwareness':
    case 'confirmDeclarations':
    case 'givePropertyDeclarations': {
      state[dispatchId] = [PARAMS_KEY, CONDITIONS_KEY, PROSPECT_STATUS_QUERY_KEY];
      return { ...state };
    }
    case 'requestQuote':
    case 'giveConsents':
    case 'enterBankDetails':
    case 'enterCreditCardDetails':
    case 'selectPolicyStartDate':
    case 'submitApplication': {
      return { ...state };
    }
    case 'enterPhoneNumber':
    case 'enterEmail':
    case 'giveTermsAndConditionsConsent':
    case 'enterCompanyName':
    case 'enterRepresentative':
    case 'enterHowDidYouHearAboutUs':
    case 'flagCarInsuranceInterest': {
      state[dispatchId] = [PARAMS_KEY];
      return { ...state };
    }
    case 'enterFoundingDate': {
      state[dispatchId] = [PARAMS_KEY, PRICING_KEY];
      return { ...state };
    }
    default:
      return assertNever(commandType);
  }
};
